import { TrendingUp, TrendingDown, Clock, DollarSign, Target, Users, ThumbsUp, AlertCircle, CheckCircle2, CircleDot } from 'lucide-react';

interface KPI {
  current: number;
  trend: number;
  status: 'At Risk' | 'On Track' | 'Excellent';
}

interface ProgramKPIs {
  schedulePerformance: KPI;
  budgetPerformance: KPI;
  scopeCompliance: KPI;
  resourceAvailability: KPI;
  stakeholderSatisfaction: KPI;
}

interface ProgramKPIIndicatorsProps {
  kpis: ProgramKPIs;
}

export function ProgramKPIIndicators({ kpis }: ProgramKPIIndicatorsProps) {
  const getStatusIcon = (status: 'At Risk' | 'On Track' | 'Excellent') => {
    switch (status) {
      case 'At Risk':
        return { Icon: AlertCircle, color: 'text-red-500' };
      case 'On Track':
        return { Icon: CircleDot, color: 'text-blue-500' };
      case 'Excellent':
        return { Icon: CheckCircle2, color: 'text-green-500' };
    }
  };

  const getIcon = (label: string) => {
    switch (label) {
      case 'Schedule Performance':
        return Clock;
      case 'Budget Performance':
        return DollarSign;
      case 'Scope Compliance':
        return Target;
      case 'Resource Availability':
        return Users;
      case 'Stakeholder Satisfaction':
        return ThumbsUp;
      default:
        return Target;
    }
  };

  const kpiData = [
    { label: 'Schedule Performance', kpi: kpis.schedulePerformance },
    { label: 'Budget Performance', kpi: kpis.budgetPerformance },
    { label: 'Scope Compliance', kpi: kpis.scopeCompliance },
    { label: 'Resource Availability', kpi: kpis.resourceAvailability },
    { label: 'Stakeholder Satisfaction', kpi: kpis.stakeholderSatisfaction }
  ];

  return (
    <div className="bg-white rounded-xl border border-[#D5D9DD]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#E8E9E9]">
        <h4 className="text-[#2A3B4C]">Key Success Factors</h4>
      </div>

      {/* Table */}
      <div className="divide-y divide-[#E8E9E9]">
        {kpiData.map(({ label, kpi }) => {
          const Icon = getIcon(label);
          const { Icon: StatusIcon, color: statusColor } = getStatusIcon(kpi.status);
          
          return (
            <div
              key={label}
              className="px-6 py-4 hover:bg-[#F8F9FA] transition-colors group"
            >
              <div className="flex items-center gap-6">
                {/* Icon & Label */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-[#E8E9E9] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#556B7C]" />
                  </div>
                  <span className="text-sm text-[#2A3B4C]">{label}</span>
                </div>

                {/* Performance Value */}
                <div className="w-24 text-right">
                  <span className="text-2xl text-[#2A3B4C]">{kpi.current}%</span>
                </div>

                {/* Trend */}
                <div className="w-20 flex items-center justify-center">
                  {kpi.trend !== 0 ? (
                    <div className={`flex items-center gap-1 ${kpi.trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {kpi.trend > 0 ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      <span className="text-sm">{Math.abs(kpi.trend)}%</span>
                    </div>
                  ) : (
                    <span className="text-sm text-[#8B9BAB]">—</span>
                  )}
                </div>

                {/* Status */}
                <div className="w-28 flex items-center gap-2">
                  <StatusIcon className={`w-4 h-4 ${statusColor}`} />
                  <span className={`text-sm ${statusColor}`}>{kpi.status}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

